import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminTimeSeries, useAdminSystemHealth } from "@/hooks/useAdminAnalytics";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { format, subDays, subHours } from "date-fns";
import { RefreshCw, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

type TimeRange = "24h" | "7d" | "30d";
type Interval = "hour" | "day" | "week";

const COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

// Calculate date range based on selection
const getDateRange = (timeRange: TimeRange) => {
  const now = new Date();
  switch (timeRange) {
    case "24h":
      return { start: subHours(now, 24), end: now };
    case "7d":
      return { start: subDays(now, 7), end: now };
    case "30d":
      return { start: subDays(now, 30), end: now };
  }
};

// Determine appropriate interval based on time range
const getIntervalForRange = (range: TimeRange): Interval => {
  switch (range) {
    case "24h":
      return "hour";
    case "7d":
      return "day";
    case "30d":
      return "day";
  }
};

export default function Analytics() {
  const [timeRange, setTimeRange] = useState<TimeRange>("7d");
  const [interval, setInterval] = useState<Interval>("day");
  const { data: timeSeriesData, loading: tsLoading, fetch: fetchTimeSeries } = useAdminTimeSeries();
  const { data: healthData, loading: healthLoading, fetch: fetchHealth } = useAdminSystemHealth();

  useEffect(() => {
    const newInterval = getIntervalForRange(timeRange);
    setInterval(newInterval);
    const { start, end } = getDateRange(timeRange);
    fetchTimeSeries(start, end, newInterval);
    fetchHealth();
  }, [timeRange, fetchTimeSeries, fetchHealth]);

  const handleRefresh = () => {
    const { start, end } = getDateRange(timeRange);
    fetchTimeSeries(start, end, interval);
    fetchHealth();
  };

  // Format data for charts
  const volumeData = (timeSeriesData?.volume || []).map((d) => ({
    ...d,
    time: formatTimeLabel(d.time, interval),
  }));

  const successRateData = (timeSeriesData?.success_rate || []).map((d) => ({
    ...d,
    time: formatTimeLabel(d.time, interval),
  }));

  const durationData = (timeSeriesData?.duration || []).map((d) => ({
    ...d,
    time: formatTimeLabel(d.time, interval),
  }));

  const usersData = (timeSeriesData?.users || []).map((d) => ({
    ...d,
    time: formatTimeLabel(d.time, interval),
  }));

  // Distribution data for pie charts
  const modalityData = (healthData?.modality_distribution || []).map((d, i) => ({
    name: d.modality || "Unknown",
    value: d.count,
    color: COLORS[i % COLORS.length],
  }));

  const tierData = (healthData?.tier_distribution || []).map((d, i) => ({
    name: `Tier ${d.tier}`,
    value: d.count,
    color: COLORS[i % COLORS.length],
  }));

  const isLoading = tsLoading || healthLoading;

  return (
    <AdminLayout
      title="Analytics"
      description="Performance metrics and trends over time"
    >
      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Last 24 hours</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
          <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Volume Chart */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Report Volume</CardTitle>
          <CardDescription>Total, successful, and failed reports over time</CardDescription>
        </CardHeader>
        <CardContent>
          {tsLoading ? (
            <ChartSkeleton />
          ) : volumeData.length === 0 ? (
            <EmptyChart message="No volume data available" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={volumeData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="time" className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <YAxis className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="total"
                  stackId="1"
                  stroke="#3b82f6"
                  fill="#3b82f680"
                  name="Total"
                />
                <Area
                  type="monotone"
                  dataKey="successful"
                  stackId="2"
                  stroke="#22c55e"
                  fill="#22c55e80"
                  name="Successful"
                />
                <Area
                  type="monotone"
                  dataKey="failed"
                  stackId="3"
                  stroke="#ef4444"
                  fill="#ef444480"
                  name="Failed"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Success Rate & Duration Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Success Rate</CardTitle>
            <CardDescription>Percentage of successful reports over time</CardDescription>
          </CardHeader>
          <CardContent>
            {tsLoading ? (
              <ChartSkeleton height={250} />
            ) : successRateData.length === 0 ? (
              <EmptyChart message="No success rate data available" height={250} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={successRateData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="time" className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis
                    domain={[0, 100]}
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip
                    formatter={(value: number) => [`${value.toFixed(1)}%`, "Success Rate"]}
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={false}
                    name="Success Rate"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Generation Duration</CardTitle>
            <CardDescription>Average and P95 duration over time</CardDescription>
          </CardHeader>
          <CardContent>
            {tsLoading ? (
              <ChartSkeleton height={250} />
            ) : durationData.length === 0 ? (
              <EmptyChart message="No duration data available" height={250} />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={durationData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="time" className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={(v) => `${v}ms`}
                  />
                  <Tooltip
                    formatter={(value: number) => [`${value.toLocaleString()}ms`]}
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="avg_ms"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                    name="Average"
                  />
                  <Line
                    type="monotone"
                    dataKey="p95_ms"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={false}
                    name="P95"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User Activity & Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Active Users</CardTitle>
            <CardDescription>Unique users per {interval}</CardDescription>
          </CardHeader>
          <CardContent>
            {tsLoading ? (
              <ChartSkeleton height={200} />
            ) : usersData.length === 0 ? (
              <EmptyChart message="No user data available" height={200} />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={usersData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="time" className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis className="text-xs" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                    }}
                  />
                  <Bar dataKey="count" fill="#8b5cf6" name="Users" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By Modality</CardTitle>
            <CardDescription>Distribution of reports by modality</CardDescription>
          </CardHeader>
          <CardContent>
            {healthLoading ? (
              <ChartSkeleton height={200} />
            ) : modalityData.length === 0 ? (
              <EmptyChart message="No modality data available" height={200} />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={modalityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {modalityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resolution Tier</CardTitle>
            <CardDescription>Distribution by template resolution tier</CardDescription>
          </CardHeader>
          <CardContent>
            {healthLoading ? (
              <ChartSkeleton height={200} />
            ) : tierData.length === 0 ? (
              <EmptyChart message="No tier data available" height={200} />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={tierData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {tierData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function formatTimeLabel(time: string, interval: Interval): string {
  const date = new Date(time);
  switch (interval) {
    case "hour":
      return format(date, "HH:mm");
    case "day":
      return format(date, "MMM d");
    case "week":
      return format(date, "MMM d");
  }
}

function ChartSkeleton({ height = 300 }: { height?: number }) {
  return (
    <div
      className="animate-pulse bg-muted rounded"
      style={{ height }}
    />
  );
}

function EmptyChart({ message, height = 300 }: { message: string; height?: number }) {
  return (
    <div
      className="flex items-center justify-center text-muted-foreground"
      style={{ height }}
    >
      {message}
    </div>
  );
}
